import { type History, HistoryEntryTypeEnum, useHistory } from "@repo/api";
import { t } from "@repo/translation";
import { translateOrderStatus } from "@repo/ui";
import { hasItems, maskText } from "@repo/utils";

import { isDropInHistory } from "./isDropInHistory";

export type HistoryEntryFilter =
  "APPOINTMENT" | "ORDER" | "PLANNER" | "TASK_UPDATE" | "FILES";

export interface HistoryGroup extends History {
  groupId: string;
  prevEntries: History[];
}

export const isHistoryGroup = (
  data: History | HistoryGroup
): data is HistoryGroup =>
  data ? Object.prototype.hasOwnProperty.call(data, "prevEntries") : false;

export const filterEntries = (filter: HistoryEntryFilter[]) => {
  return (item: History | HistoryGroup) => {
    if (filter.length === 0) {
      return true;
    }

    if (filter.includes("APPOINTMENT")) {
      if (isAppointmentEntry(item)) {
        return true;
      }
    }

    if (filter.includes("ORDER")) {
      if (
        item.entryType === HistoryEntryTypeEnum.ORDER ||
        item.entryType === HistoryEntryTypeEnum.ORDER_UPDATE ||
        item.entryType === HistoryEntryTypeEnum.B2B_ENR_FULFILLMENT_UPDATE ||
        item.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_IMPORTED ||
        item.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_DELETED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_IMPORTED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_DELETED ||
        item.entryType === HistoryEntryTypeEnum.QUOTATION_ASSOCIATED ||
        item.entryType === HistoryEntryTypeEnum.QUOTATION_DELETED ||
        item.entryType === HistoryEntryTypeEnum.QUOTATION_CANCELLED ||
        item.entryType === HistoryEntryTypeEnum.QUOTATION_CONVERTED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_CANCELLED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_COMPLETED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_ASSOCIATED ||
        item.entryType === HistoryEntryTypeEnum.ORDER_CONVERTED ||
        item.entryType === HistoryEntryTypeEnum.DUPLICATE_ORDER_ASSOCIATED ||
        item.entryType ===
          HistoryEntryTypeEnum.DRAFT_ORDER_CONVERTED_TO_QUOTATION
      ) {
        return true;
      }
    }
    if (filter.includes("PLANNER")) {
      if (
        item.entryType === HistoryEntryTypeEnum.PLANNER ||
        item.entryType === HistoryEntryTypeEnum.PLANNER_ASSOCIATED ||
        item.entryType === HistoryEntryTypeEnum.PLANNER_DELETED ||
        item.entryType === HistoryEntryTypeEnum.PLANNER_CONVERTED ||
        item.entryType ===
          HistoryEntryTypeEnum.PLANNER_CONVERTED_TO_QUOTATION ||
        item.entryType === HistoryEntryTypeEnum.PLANNER_CONVERTED_TO_DRAFT_ORDER
      ) {
        return true;
      }
    }

    if (filter.includes("TASK_UPDATE")) {
      const notDropIn = !isDropInHistory(item);

      if (
        (item.entryType === HistoryEntryTypeEnum.TASK_UPDATE && notDropIn) ||
        (item.entryType === HistoryEntryTypeEnum.TASK_CREATED && notDropIn) ||
        (item.entryType === HistoryEntryTypeEnum.TASK_REMOVED && notDropIn)
      ) {
        return true;
      }
    }

    if (filter.includes("FILES")) {
      if (
        item.entryType === HistoryEntryTypeEnum.FILE_UPLOADED ||
        item.entryType === HistoryEntryTypeEnum.FILE_DOWNLOADED ||
        item.entryType === HistoryEntryTypeEnum.FILE_SHARED ||
        item.entryType === HistoryEntryTypeEnum.FILE_UNSHARED
      ) {
        return true;
      }
    }

    return false;
  };
};

const sortHistoryItems = (
  a: History | HistoryGroup,
  b: History | HistoryGroup
) => {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
};

export const isAppointmentEntry = (history: History): boolean => {
  return (
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_CREATED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_ASSIGNED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_STARTED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_RESCHEDULED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_CANCELLED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_COMPLETED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_PAID ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_NO_SHOW ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_REASSIGNED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_DETACHED ||
    history.entryType === HistoryEntryTypeEnum.APPOINTMENT_ATTACHED ||
    history.entryType === HistoryEntryTypeEnum.FOLLOWUP_APPOINTMENT_CREATED ||
    isDropInHistory(history)
  );
};

export const getHistoryId = (history: History) => {
  // Todo: add more group logic here

  if (
    history.entryType === HistoryEntryTypeEnum.ORDER ||
    history.entryType === HistoryEntryTypeEnum.ORDER_UPDATE ||
    history.entryType === HistoryEntryTypeEnum.B2B_ENR_FULFILLMENT_UPDATE ||
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_DELETED ||
    history.entryType === HistoryEntryTypeEnum.ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.ORDER_DELETED
  ) {
    return (
      history.details?.order_number ??
      getIdInTitle(history.title) ??
      getIdInTitle(history.body) ??
      history.id
    );
  } else if (
    history.entryType === HistoryEntryTypeEnum.QUOTATION_ASSOCIATED ||
    history.entryType === HistoryEntryTypeEnum.QUOTATION_DELETED
  ) {
    return (
      history.details?.quotation_id ??
      getIdInTitle(history.title) ??
      getIdInTitle(history.body) ??
      history.id
    );
  } else if (isAppointmentEntry(history)) {
    return (
      getIdInTitle(history.title) ??
      history.details?.appointment_order_number ??
      history.details?.order_number ??
      history.id
    );
  } else if (history.entryType === HistoryEntryTypeEnum.PLANNER) {
    return getDrawingIdInTitle(history.title) ?? history.id;
  } else if (history.entryType === HistoryEntryTypeEnum.TASK_UPDATE) {
    return history.details?.task_id ?? history.id;
  } else {
    return history.id;
  }
};

const getGroupHistoryId = (history: History) => {
  const id = getHistoryId(history);
  if (
    history.entryType === HistoryEntryTypeEnum.ORDER ||
    history.entryType === HistoryEntryTypeEnum.ORDER_UPDATE ||
    history.entryType === HistoryEntryTypeEnum.B2B_ENR_FULFILLMENT_UPDATE ||
    history.entryType === HistoryEntryTypeEnum.ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.ORDER_DELETED
  ) {
    return `ORDER_${id}`;
  } else if (
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_IMPORTED ||
    history.entryType === HistoryEntryTypeEnum.DRAFT_ORDER_DELETED
  ) {
    return `DRAFT_ORDER_${id}`;
  } else if (
    history.entryType === HistoryEntryTypeEnum.QUOTATION_ASSOCIATED ||
    history.entryType === HistoryEntryTypeEnum.QUOTATION_DELETED
  ) {
    return `QUOTATION_${id}`;
  } else if (isAppointmentEntry(history)) {
    return `APPOINTMENT_${id}`;
  } else if (
    history.entryType === HistoryEntryTypeEnum.PLANNER ||
    history.entryType === HistoryEntryTypeEnum.PLANNER_ASSOCIATED
  ) {
    return `PLANNER_${id}`;
  } else if (history.entryType === HistoryEntryTypeEnum.TASK_UPDATE) {
    return `TASK_${id}`;
  } else {
    return id;
  }
};

export const groupHistoryEntries = (histories: History[]) => {
  if (!hasItems(histories)) {
    return [];
  }
  const grouped = [] as HistoryGroup[];

  histories.sort(sortHistoryItems).forEach((history) => {
    const id = getGroupHistoryId(history);
    const existing = grouped.find((el) => el.groupId === id);
    if (existing) {
      existing.prevEntries.push(history);
    } else {
      grouped.push({
        ...history,
        groupId: id,
        prevEntries: [history],
      });
    }
  });

  return grouped;
};

// TODO: This is a temporary function to get ids from title
const getIdInTitle = (title?: string): string | null => {
  if (!title) {
    return null;
  }
  const regex = /\d{7,}/;
  const matches = regex.exec(title);
  return matches ? matches[0] : null;
};

const getDrawingIdInTitle = (title?: string): string | null => {
  if (!title) {
    return null;
  }
  return title.split(": ")?.[1];
};

export const getOrderStatusUpdate = (history: History) => {
  const entry = history.details?.updates?.find(
    (el) => el.field === "orderInformation/orderStatus" || el.field === "status"
  );
  if (entry?.old_value && entry?.new_value) {
    return t("prompt.order_status_update", {
      oldStatus: translateOrderStatus(entry.old_value),
      newStatus: translateOrderStatus(entry.new_value),
    });
  } else {
    return undefined;
  }
};

export const useGroupHistoryEntry = (
  opportunityId?: number,
  groupId?: string
) => {
  const { data: histories } = useHistory(opportunityId);

  if (!opportunityId || !groupId) {
    return undefined;
  }
  const entries = groupHistoryEntries(histories ?? []);
  return entries.find((history) => history.groupId === groupId)?.prevEntries;
};

export const maskHistoryEntries = (entries?: History[]) => {
  return (
    entries?.map((entry) => ({
      ...entry,
      title: maskText(entry.title) ?? "",
      body: maskText(entry.body),
    })) ?? []
  );
};
